/**
 * useJourneyProgress Hook
 * Tracks user's progress through the learning journey
 * Determines which sidebar sections should be unlocked
 */

import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  selectCurrentCompany,
  selectCurrentUser,
} from "../store/slices/authSlice";
import {
  useGetTemplatesForEmployeeQuery,
  useGetEmployeeSubmissionsQuery,
  useGetEmployeeDiagnosticAssignmentsQuery,
  useGetEmployeeDiagnosticSubmissionsQuery,
} from "../services/api";

// Cache key for localStorage
const JOURNEY_CACHE_KEY = "journeyProgress";

// Pages that need fresh journey data
const JOURNEY_PAGES = ["/dashboard", "/values", "/diagnostic", "/development-plans"];

/**
 * Hook to check if user has completed all assigned Values forms
 * @param {Object} options - Options for the hook
 * @param {boolean} options.forceFetch - Force fetch even on non-journey pages
 * @returns {Object} Progress status and counts
 */
export const useJourneyProgress = (options = {}) => {
  const { forceFetch = false } = options;
  const location = useLocation();
  const company = useSelector(selectCurrentCompany);
  const user = useSelector(selectCurrentUser);

  const companyId = company?._id || company?.id;
  const jobLevel = user?.jobLevel || user?.companyRole;

  // Only fetch on journey-related pages or when forced
  const isJourneyPage = JOURNEY_PAGES.some(page => location.pathname.startsWith(page));
  const shouldFetch = forceFetch || isJourneyPage;

  // ==================== VALUES DATA ====================
  // Get templates assigned to this employee
  const {
    data: templates,
    isLoading: templatesLoading,
  } = useGetTemplatesForEmployeeQuery(
    { companyId, jobLevel },
    { skip: !companyId || !shouldFetch }
  );

  // Get all submissions for this employee
  const {
    data: submissions,
    isLoading: submissionsLoading,
  } = useGetEmployeeSubmissionsQuery(undefined, {
    skip: !companyId || !shouldFetch,
  });

  // ==================== DIAGNOSTICS DATA ====================
  // Get diagnostic assignments for this employee
  const {
    data: diagnosticAssignments,
    isLoading: diagnosticAssignmentsLoading,
  } = useGetEmployeeDiagnosticAssignmentsQuery(undefined, {
    skip: !companyId || !shouldFetch,
  });

  // Get diagnostic submissions for this employee
  const {
    data: diagnosticSubmissions,
    isLoading: diagnosticSubmissionsLoading,
  } = useGetEmployeeDiagnosticSubmissionsQuery(undefined, {
    skip: !companyId || !shouldFetch,
  });

  const isLoading = shouldFetch && (templatesLoading || submissionsLoading ||
    diagnosticAssignmentsLoading || diagnosticSubmissionsLoading);

  // ==================== VALUES COMPLETION ====================
  // Count assigned templates
  const assignedCount = templates?.length || 0;

  // Count completed (submitted) forms
  const completedCount = (submissions || []).filter(
    (s) => s.status === "submitted"
  ).length;

  // Values are complete when:
  // 1. User has at least one assignment AND
  // 2. All assigned forms have been submitted
  const valuesComplete = assignedCount > 0 && completedCount >= assignedCount;

  // ==================== DIAGNOSTICS COMPLETION ====================
  // Get the assignments array (handle both array and object response)
  const diagAssignments = Array.isArray(diagnosticAssignments)
    ? diagnosticAssignments
    : diagnosticAssignments?.assignments || [];

  // Get the submissions array
  const diagSubmissions = Array.isArray(diagnosticSubmissions)
    ? diagnosticSubmissions
    : diagnosticSubmissions?.submissions || [];

  // Count diagnostic assignments
  const diagnosticAssignedCount = diagAssignments.length;

  // Count completed diagnostic submissions (status = "submitted" or "completed")
  const diagnosticCompletedCount = diagSubmissions.filter(
    (s) => s.status === "submitted" || s.status === "completed"
  ).length;

  // Diagnostics are complete when:
  // 1. User has at least one diagnostic assignment AND
  // 2. All assigned diagnostics have been submitted
  const diagnosticsComplete = diagnosticAssignedCount > 0 &&
    diagnosticCompletedCount >= diagnosticAssignedCount;

  // ==================== STEP UNLOCK LOGIC ====================
  // Step 2 (Diagnostics): Unlocks after values are complete
  const step2Unlocked = valuesComplete;

  // Step 3 (Development Plans): Unlocks after diagnostics are complete
  const step3Unlocked = valuesComplete && diagnosticsComplete;

  // Step 4 (Competency): Unlocks after diagnostics are complete
  const step4Unlocked = valuesComplete && diagnosticsComplete;

  // Build the progress object
  const progressData = {
    valuesComplete,
    diagnosticsComplete,
    step2Unlocked,
    step3Unlocked,
    step4Unlocked,
    assignedCount,
    completedCount,
    diagnosticAssignedCount,
    diagnosticCompletedCount,
  };

  // Cache progress when we fetch fresh data
  useEffect(() => {
    if (shouldFetch && !isLoading && companyId) {
      try {
        localStorage.setItem(JOURNEY_CACHE_KEY, JSON.stringify(progressData));
      } catch (e) {
        // Ignore localStorage errors
      }
    }
  }, [shouldFetch, isLoading, companyId, valuesComplete, diagnosticsComplete]);

  // If not fetching, try to use cached data
  if (!shouldFetch) {
    try {
      const cached = localStorage.getItem(JOURNEY_CACHE_KEY);
      if (cached) {
        const cachedProgress = JSON.parse(cached);
        return {
          ...cachedProgress,
          isLoading: false,
        };
      }
    } catch (e) {
      // Ignore parse errors
    }
    // No cache - return defaults (all unlocked to avoid blocking UI)
    return {
      valuesComplete: false,
      diagnosticsComplete: false,
      step2Unlocked: true, // Default to unlocked when no data
      step3Unlocked: true,
      step4Unlocked: true,
      assignedCount: 0,
      completedCount: 0,
      diagnosticAssignedCount: 0,
      diagnosticCompletedCount: 0,
      isLoading: false,
    };
  }

  return {
    ...progressData,
    isLoading,
  };
};

export default useJourneyProgress;
